import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowsClockwise, Shield, ClockCounterClockwise, LockSimple, CircleDashed, Flag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsistencyBadge = ({ label }) => {
  if (!label) return <span className="text-[#98A2B3] text-xs">-</span>;
  const consistent = label === "Consistent";
  return (
    <Badge className={consistent ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"}>
      {label}
    </Badge>
  );
};

const InfoField = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-[#98A2B3]">{label}</div>
    <div className="text-sm font-medium text-[#101828] mt-0.5">{value ?? "-"}</div>
  </div>
);

export default function BooDetailPage() {
  const { booId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditDepartment = user?.role === "super_admin" || user?.role === "admin";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [workCenters, setWorkCenters] = useState([]);
  const [savingOp, setSavingOp] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/boo/${encodeURIComponent(booId)}`);
      setDetail(data);
    } catch (err) {
      toast.error("Could not load Bill of Operations", { description: err?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/admin/work-centers`).then(({ data }) => setWorkCenters(data.work_centers || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booId]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/admin/boo/${encodeURIComponent(booId)}/refresh`);
      setDetail(data);
      toast.success("Refreshed from SAP");
    } catch (err) {
      toast.error("Refresh failed", { description: err?.response?.data?.detail });
    } finally {
      setRefreshing(false);
    }
  };

  const setDepartment = async (operationId, department) => {
    setSavingOp(operationId);
    try {
      const { data } = await axios.put(
        `${API}/admin/boo/${encodeURIComponent(booId)}/operations/${encodeURIComponent(operationId)}/department`,
        { department: department === "__none__" ? null : department }
      );
      setDetail(data);
      toast.success(`${operationId} -> ${department === "__none__" ? "Unassigned" : department}`);
    } catch (err) {
      toast.error("Could not update Department", { description: err?.response?.data?.detail });
    } finally {
      setSavingOp(null);
    }
  };

  const loadAudit = async () => {
    setShowAudit((v) => !v);
    if (showAudit) return;
    try {
      const { data } = await axios.get(`${API}/admin/boo/${encodeURIComponent(booId)}/audit-log`);
      setAudit(data.entries || []);
    } catch {
      setAudit([]);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Bill of Operations</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="bg-white border-b border-[#D0D5DD] p-3 flex items-center gap-3 shrink-0">
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate("/admin/boo")} data-testid="boo-detail-back-button">
          <ArrowLeft size={13} className="mr-1.5" /> Back to List
        </Button>
        <span className="font-mono text-sm font-semibold text-[#101828]" data-testid="boo-detail-title">{booId}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={loadAudit} data-testid="boo-detail-audit-toggle">
            <ClockCounterClockwise size={13} className="mr-1.5" /> {showAudit ? "Hide" : "View"} Audit Log
          </Button>
          <Button size="sm" className="h-8 bg-[#0E7C86] hover:bg-[#0B6570] text-xs" disabled={refreshing} onClick={refresh} data-testid="boo-detail-refresh-button">
            <ArrowsClockwise size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh from SAP
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {loading ? (
          <div className="text-sm text-[#667085] p-6 text-center">Loading...</div>
        ) : !detail ? (
          <div className="text-sm text-[#667085] p-6 text-center">Bill of Operations not found.</div>
        ) : (
          <>
            <div className="bg-white border border-[#D0D5DD] rounded-lg p-4" data-testid="boo-detail-header-card">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <InfoField label="BOO ID" value={detail.boo_id} testId="boo-detail-field-id" />
                <InfoField label="Description" value={detail.description} testId="boo-detail-field-description" />
                <InfoField label="Site" value={detail.site_id && detail.site_name ? `${detail.site_id} - ${detail.site_name}` : detail.site_id} testId="boo-detail-field-site" />
                <InfoField label="Quantity" value={detail.quantity != null ? `${detail.quantity} ${detail.unit || ""}` : "-"} testId="boo-detail-field-quantity" />
                <InfoField label="Task Generation Method" value={detail.task_generation_method_label} testId="boo-detail-field-task-gen" />
                <div data-testid="boo-detail-field-planning-consistency">
                  <div className="text-[11px] uppercase tracking-wide text-[#98A2B3]">Planning Consistency</div>
                  <div className="mt-1"><ConsistencyBadge label={detail.planning_consistency_label} /></div>
                </div>
                <div data-testid="boo-detail-field-execution-consistency">
                  <div className="text-[11px] uppercase tracking-wide text-[#98A2B3]">Execution Consistency</div>
                  <div className="mt-1"><ConsistencyBadge label={detail.execution_consistency_label} /></div>
                </div>
                <InfoField
                  label="Last Synced"
                  value={detail.last_synced_at ? `${new Date(detail.last_synced_at).toLocaleString()} by ${detail.last_synced_by || "-"}` : "-"}
                  testId="boo-detail-field-last-synced"
                />
              </div>
            </div>

            {showAudit && (
              <div className="bg-white border border-[#D0D5DD] rounded-lg p-4" data-testid="boo-detail-audit-panel">
                <div className="text-sm font-semibold text-[#101828] mb-2">Audit Trail</div>
                {audit.length === 0 ? (
                  <div className="text-xs text-[#98A2B3]">No changes recorded yet.</div>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-auto">
                    {audit.map((a, i) => (
                      <div key={i} className="text-xs text-[#475467] flex items-center gap-2" data-testid={`boo-audit-entry-${i}`}>
                        <span className="text-[#98A2B3] shrink-0">{new Date(a.changed_at).toLocaleString()}</span>
                        <span className="font-medium">{a.changed_by}</span>
                        {a.action === "department_changed" ? (
                          <span>changed <span className="font-mono">{a.operation_id}</span> Department: {a.previous_value || "Unassigned"} -&gt; {a.new_value || "Unassigned"}</span>
                        ) : (
                          <span>{a.action === "boo_created" ? "fetched this BOO from SAP for the first time" : "re-synced this BOO from SAP"}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="bg-white border border-[#D0D5DD] rounded-lg p-4">
              <div className="text-sm font-semibold text-[#101828] mb-3">Operation Structure</div>
              <div className="space-y-2" data-testid="boo-detail-structure">
                <div className="flex items-center gap-2 text-xs text-[#98A2B3] font-mono pl-1">
                  <CircleDashed size={13} /> START
                </div>
                {detail.operations.map((op) => (
                  <div key={op.operation_id} className="border border-[#EAECF0] rounded-lg" data-testid={`boo-operation-card-${op.operation_id}`}>
                    <div className="p-3 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                      <InfoField label="Operation ID" value={op.operation_id} testId={`boo-op-field-id-${op.operation_id}`} />
                      <InfoField label="Description" value={op.operation_description} testId={`boo-op-field-description-${op.operation_id}`} />
                      <InfoField label="Type / Activity" value={`${op.operation_type_label || "-"} / ${op.activity_id || "-"} (${op.activity_type_label || "-"})`} testId={`boo-op-field-type-${op.operation_id}`} />
                      <InfoField label="Main Resource" value={op.main_resource_id ? `${op.main_resource_id}${op.main_resource_description ? " - " + op.main_resource_description : ""}` : "-"} testId={`boo-op-field-resource-${op.operation_id}`} />
                      <InfoField label="Duration (Var / Fixed)" value={`${op.variable_duration || "-"} / ${op.fixed_duration || "-"}`} testId={`boo-op-field-duration-${op.operation_id}`} />
                    </div>
                    <div className="px-3 pb-3 flex items-center gap-3">
                      <span className="text-[11px] uppercase tracking-wide text-[#98A2B3]">Department / Work Center</span>
                      <Select
                        value={op.department || "__none__"}
                        disabled={!canEditDepartment || savingOp === op.operation_id}
                        onValueChange={(v) => setDepartment(op.operation_id, v)}
                      >
                        <SelectTrigger className="h-8 w-56 text-xs" data-testid={`boo-op-department-select-${op.operation_id}`}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Unassigned</SelectItem>
                          {workCenters.map((wc) => (
                            <SelectItem key={wc} value={wc}>{wc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!op.department && (
                        <span className="text-xs text-[#B54708] flex items-center gap-1" data-testid={`boo-op-unmapped-warning-${op.operation_id}`}>
                          <LockSimple size={11} weight="bold" /> Open to everyone until a Department is assigned
                        </span>
                      )}
                    </div>
                    {op.reporting_point_id && (
                      <div className="flex items-center gap-2 text-xs text-[#98A2B3] font-mono pl-4 pb-2 border-t border-dashed border-[#EAECF0] pt-2 mx-3">
                        <Flag size={13} /> {op.reporting_point_id}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
